/**
 * Simplified Flowise service for AI interactions
 * Replaces the complex flow selection logic from AgentChatMessageService
 */

export interface FlowConfig {
  id: string;
  name: string;
  endpoint: string;
  apiKey: string;
  organizationTypes: string[];
}

export interface FlowiseRequest {
  question: string;
  sessionId: string; // mantido para compatibilidade com o app (não vai no body novo)
  organizationId?: string; // reservado para uso futuro (dinamizar farm_id)
  organizationType?: string;
  selectedFlowId?: string; // mantido para seleção de flow
  context?: Record<string, any>; // reservado para uso futuro
  signal?: AbortSignal;
}

export interface PermissionsFlowiseRequest {
  owner_id: string;
  business_unit_type: string;
  business_unit_id: string;
  manager_type: string;
  question: string;
  sessionId: string;
  // Optional fields for Gestor Animal
  muzzle_biometrics_base64?: string;
  cadastro_inserir_biometria?: string; // keep as string to align with API example
  cattle_tag?: string;
  // Optional fields for Gestor Laboratorial (exames)
  insertExam?: string; // "true" | "false" como string
  examType?: string; // e.g. "mastitis_test"
  cattleTag?: string; // identificação do animal
  collectionDate?: string; // YYYY-MM-DD
  examDate?: string; // YYYY-MM-DD
  attachments?: { type: string; url: string }[]; // URLs em string
  examData?: string | {
    staphPrimary?: string;
    staphSecondary?: string;
    strepPrimary?: string;
    strepSecondary?: string;
    generalPrimary?: string;
    generalSecondary?: string;
    milkAspect?: string[];
    udderSigns?: string[];
    teatsAffected?: string[];
    observation?: string;
    situation?: string;
    mainTreatment?: string;
    supportiveTreatment?: string;
    recommendedAntibiotics?: string;
    treatmentPlan?: {
      situacao?: string;
      condutaPrincipal?: string;
      suporte?: string;
      antibioticos?: string;
      aines?: string[];
    } | null;
  };
  // Campos adicionais para o gestor laboratorial
  plateId?: string; // ID da placa de exame
  examResult?: string; // Resultado do exame (ex: "Staphylococcus aureus")
}

import {
  AgentResponse,
  isAgentResponseError,
  extractChatText,
  extractSessionId,
} from "../types/agentResponse";
import { apiClientBff } from "../../../services/apiBff";

export interface FlowiseResponse {
  text: string;
  sessionId?: string;
  sourceDocuments?: any[];
  agentReasoning?: any[];
  rawResponse?: AgentResponse; // Add raw response for debugging
  flowUsed?: { id: string; name: string };
}

export interface ProcessMessageResult {
  success: boolean;
  response?: FlowiseResponse;
  error?: string;
}

// No hardcoded flows - everything comes from API

export interface ManagerData {
  url: string;
  manager_type: string;
  name: string;
  token?: string;
}

export interface DynamicManagersResponse {
  data: ManagerData[];
  resumo: string;
  url: string;
}

export class FlowiseService {
  private dynamicManagersCache: FlowConfig[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  // Token removed - managed by BFF

  // Normaliza strings para comparação robusta (remove acentos, troca '_' por espaço, lowercase)
  private normalizeStr(s: string): string {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/_/g, ' ')
      .trim();
  }

  /**
   * Fetch dynamic managers from API when chat opens
   */
  async fetchDynamicManagers(userId: string): Promise<FlowConfig[]> {
    try {
      // Check cache first
      if (this.dynamicManagersCache && (Date.now() - this.cacheTimestamp < this.CACHE_DURATION)) {
         return this.dynamicManagersCache;
      }

      const response = await apiClientBff.post<{ success: boolean; flows: any[] }>('/flowise/flows', { userId });
      
      if (response.success && Array.isArray(response.flows)) {
          // Map and normalize flow data
          const mappedFlows: FlowConfig[] = response.flows.map(f => ({
              id: f.id,
              name: f.name,
              endpoint: f.endpoint || '',
              apiKey: f.apiKey || '',
              // Map singular organizationType to plural organizationTypes or default to empty array
              organizationTypes: Array.isArray(f.organizationTypes) ? f.organizationTypes : (f.organizationTypes ? [f.organizationTypes] : (Array.isArray(f.organizationType) ? f.organizationType : (f.organizationType ? [f.organizationType] : [])))
          }));

          // Sort and cache
          const sorted = this.sortManagersByPriority(mappedFlows);
          this.dynamicManagersCache = sorted;
          this.cacheTimestamp = Date.now();
          return sorted;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching dynamic managers from BFF:', error);
      return [];
    }
  }

  /**
   * Get manager ID from manager_type
   */
  private getManagerIdFromType(managerType: string): string {
    const typeMap: Record<string, string> = {
      'gestor_animal': 'animal',
      'gestor_ordenha': 'dairy',
      'gestor_perfil_animal': 'animal',
      'perfil_animal': 'animal',
      'perfil animal': 'animal',
      'gestor perfil animal': 'animal',
      'gestor_sanidade': 'health',
      'gestor_nutricao': 'nutrition',
      'nutricao': 'nutrition',
      'permissions_manager': 'permissions',
      'gestor_permissoes': 'permissions',
      'gestor_lotes': 'batch',
      'gestor_lote': 'batch',
      'gestor_pastos': 'pastos',
      'gestor_reproducao': 'reproducao',
      'gestor_laboratorial': 'laboratory',
      'gestor_laboratorio': 'laboratory',
      'laboratorio': 'laboratory',
      'gestor_master': 'master',
      'master': 'master'
    };
    return typeMap[managerType] || managerType;
  }

  /**
   * Infer manager ID from natural language question when manager_type is not specific
   */
  private inferManagerIdFromQuestion(question?: string): string | null {
    const q = this.normalizeStr(question || '');
    if (!q) return null;

    // Pasture-related intents
    if (
      q.includes('pasto') ||
      q.includes('piquete') ||
      q.includes('pasture') ||
      q.includes('mover para pasto') ||
      (q.includes('adicionar') && q.includes('lote') && q.includes('pasto')) ||
      (q.includes('transferir') && q.includes('lote') && q.includes('pasto'))
    ) {
      return 'pastos';
    }

    // Batch/Lot-related intents (fallback when not explicitly pasture)
    if (q.includes('lote') || q.includes('lotes') || q.includes('batch')) {
      return 'batch';
    }

    // Animal intents
    if (
      q.includes('animal') ||
      q.includes('passaporte') ||
      q.includes('pesagem') ||
      q.includes('registro do animal')
    ) {
      return 'animal';
    }

    // Nutrition intents
    if (q.includes('nutricao') || q.includes('nutrição') || q.includes('racao') || q.includes('ração')) {
      return 'nutrition';
    }

    // Health intents
    if (q.includes('sanidade') || q.includes('saude') || q.includes('saúde') || q.includes('vacina') || q.includes('medic')) {
      return 'health';
    }

    // Dairy intents
    if (q.includes('ordenha') || q.includes('leite') || q.includes('producao de leite') || q.includes('produção de leite')) {
      return 'dairy';
    }

    // Reproduction intents
    if (q.includes('reproducao') || q.includes('reprodução') || q.includes('inseminacao') || q.includes('inseminação') || q.includes('monta')) {
      return 'reproducao';
    }

    // Permissions
    if (q.includes('permissao') || q.includes('permissão') || q.includes('autorizar') || q.includes('grant')) {
      return 'permissions';
    }

    return null;
  }

  /**
   * Sort managers by priority order: animal → batch → nutrition → health → pastos → dairy → permissions
   */
  private sortManagersByPriority(flows: FlowConfig[]): FlowConfig[] {
    const priorityOrder = [
      'animal',      // Gestor Animal
      'batch',       // Gestor Lotes
      'nutrition',   // Gestor Nutrição
      'reproducao',  // Gestor Reprodução
      'health',      // Gestor Sanidade
      'pastos',      // Gestor Pastos
      'dairy',       // Gestor Ordenha
      'permissions'  // Gestor Permissões
    ];

    return flows.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.id);
      const indexB = priorityOrder.indexOf(b.id);
      
      // If both are in the priority list, sort by priority
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // If only one is in the priority list, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // If neither is in the priority list, maintain original order
      return 0;
    });
  }

  /**
   * Helper to send message via BFF
   */
  private async sendMessageToBFF(flowId: string, payload: any): Promise<ProcessMessageResult> {
      try {
        const result = await apiClientBff.post<{ success: boolean; data: any }>('/flowise/message', {
            flowId,
            payload
        });
        if (result.success) {
             const flowiseResp = result.data;
             return {
                 success: true,
                 response: {
                     text: flowiseResp.text || flowiseResp.message || "No response received",
                     sessionId: flowiseResp.sessionId,
                     sourceDocuments: flowiseResp.sourceDocuments,
                     agentReasoning: flowiseResp.agentReasoning,
                     rawResponse: flowiseResp,
                     flowUsed: { id: flowId, name: flowId }
                 }
             };
        }
        return { success: false, error: "BFF request failed" };
      } catch (e) {
          return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
      }
  }

  /**
   * Process message with specific manager using dynamic URL
   */
  private async processSpecificManager(
    flow: FlowConfig,
    request: PermissionsFlowiseRequest
  ): Promise<ProcessMessageResult> {
    try {
      let payload: any;

      const isLaboratoryManager =
        this.normalizeStr(flow.id).includes('laborator') ||
        this.normalizeStr(flow.name).includes('laborator');

      if (isLaboratoryManager) {
        // Detecta se é exame de mastite ou apenas chat
        const isMastitisExam = request.insertExam === "true";
        
        if (isMastitisExam) {
          // Parse examData se for string JSON
          let parsedExamData: any = {};
          if (typeof request.examData === 'string') {
            try {
              parsedExamData = JSON.parse(request.examData);
            } catch (e) {
              console.error('Erro ao fazer parse do examData:', e);
              parsedExamData = {};
            }
          } else if (request.examData) {
            parsedExamData = request.examData;
          }

          // Payload para Gestor Laboratorial (exames de mastite)
          payload = {
            form: {
              user_id: request.owner_id,
              business_unit_type: "farm",
              business_unit_id: request.business_unit_id,
              question: request.question ?? "",
              insertExam: request.insertExam ?? "false",
              examType: request.examType ?? "mastitis_test",
              cattleTag: request.cattleTag ?? '',
              collectionDate: request.collectionDate ?? '',
              examDate: request.examDate ?? '',
              attachments: JSON.stringify([{ type: "image", url: (request.attachments?.[0]?.url ?? "") }]),
              examData: JSON.stringify({
                staphPrimary: parsedExamData?.staphPrimary ?? '',
                staphSecondary: parsedExamData?.staphSecondary ?? '',
                strepPrimary: parsedExamData?.strepPrimary ?? '',
                strepSecondary: parsedExamData?.strepSecondary ?? '',
                generalPrimary: parsedExamData?.generalPrimary ?? '',
                generalSecondary: parsedExamData?.generalSecondary ?? '',
                milkAspect: parsedExamData?.milkAspect ?? ["grumos"],
                udderSigns: parsedExamData?.udderSigns ?? [],
                teatsAffected: parsedExamData?.teatsAffected ?? [],
                observation: parsedExamData?.observation ?? '',
                situation: parsedExamData?.situation ?? parsedExamData?.treatmentPlan?.situacao ?? '',
                mainTreatment: parsedExamData?.mainTreatment ?? parsedExamData?.treatmentPlan?.condutaPrincipal ?? '',
                supportiveTreatment: parsedExamData?.supportiveTreatment ?? parsedExamData?.treatmentPlan?.suporte ?? '',
                recommendedAntibiotics: parsedExamData?.recommendedAntibiotics ?? parsedExamData?.treatmentPlan?.antibioticos ?? '',
                treatmentPlan: parsedExamData?.treatmentPlan ?? null,
              }),
              plateId: request.plateId ?? "",
              examResult: request.examResult ?? ""
            },
            streaming: false,
            overrideConfig: {
              sessionId: request.sessionId,
              vars: {
                user_id: request.owner_id,
                business_unit_type: "farm",
                business_unit_id: request.business_unit_id,
                question: request.question ?? "",
                insertExam: request.insertExam ?? "false",
                examType: request.examType ?? "mastitis_test",
                cattleTag: request.cattleTag ?? '',
                collectionDate: request.collectionDate ?? '',
                examDate: request.examDate ?? '',
                attachments: JSON.stringify([{ type: "image", url: (request.attachments?.[0]?.url ?? "") }]),
                examData: JSON.stringify({
                  staphPrimary: parsedExamData?.staphPrimary ?? '',
                  staphSecondary: parsedExamData?.staphSecondary ?? '',
                  strepPrimary: parsedExamData?.strepPrimary ?? '',
                  strepSecondary: parsedExamData?.strepSecondary ?? '',
                  generalPrimary: parsedExamData?.generalPrimary ?? '',
                  generalSecondary: parsedExamData?.generalSecondary ?? '',
                  milkAspect: parsedExamData?.milkAspect ?? ["grumos"],
                  udderSigns: parsedExamData?.udderSigns ?? [],
                  teatsAffected: parsedExamData?.teatsAffected ?? [],
                  observation: parsedExamData?.observation ?? '',
                  situation: parsedExamData?.situation ?? parsedExamData?.treatmentPlan?.situacao ?? '',
                  mainTreatment: parsedExamData?.mainTreatment ?? parsedExamData?.treatmentPlan?.condutaPrincipal ?? '',
                  supportiveTreatment: parsedExamData?.supportiveTreatment ?? parsedExamData?.treatmentPlan?.suporte ?? '',
                  recommendedAntibiotics: parsedExamData?.recommendedAntibiotics ?? parsedExamData?.treatmentPlan?.antibioticos ?? '',
                  treatmentPlan: parsedExamData?.treatmentPlan ?? null,
                }),
                plateId: request.plateId ?? "",
                examResult: request.examResult ?? ""
              }
            }
          };
        } else {
          // Payload simplificado para Gestor Laboratorial (chat normal, não exame)
          payload = {
            form: {
              user_id: request.owner_id,
              business_unit_type: "farm",
              business_unit_id: request.business_unit_id,
              question: request.question ?? "",
              insertExam: "false"
            },
            streaming: false,
            overrideConfig: {
              sessionId: request.sessionId,
              vars: {
                user_id: request.owner_id,
                business_unit_type: "farm",
                business_unit_id: request.business_unit_id,
                question: request.question ?? "",
                insertExam: "false"
              }
            }
          };
        }
      } else if (flow.id === 'animal') {
        // Payload para Gestor Animal no formato padrão, com campos adicionais
        payload = {
          form: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
            question: request.question,
            muzzle_biometrics_base64: request.muzzle_biometrics_base64 ?? '',
            cadastro_inserir_biometria: request.cadastro_inserir_biometria ?? '',
            cattle_tag: request.cattle_tag ?? '',
          },
          streaming: false,
          overrideConfig: {
            sessionId: request.sessionId,
            vars: {
              user_id: request.owner_id,
              business_unit_type: "farm",
              business_unit_id: request.business_unit_id,
              muzzle_biometrics_base64: request.muzzle_biometrics_base64 ?? '',
              cadastro_inserir_biometria: request.cadastro_inserir_biometria ?? '',
              cattle_tag: request.cattle_tag ?? '',
            },
          },
        };
        
      } else if (flow.id === 'gestor_master' || flow.id === 'master') {
        // Payload para Gestor Master
        payload = {
          form: {
            user_id: request.owner_id,
            business_unit_type: request.business_unit_type,
            business_unit_id: request.business_unit_id,
            question: request.question,
          },
          streaming: false,
          overrideConfig: {
            sessionId: request.sessionId,
            vars: {
              user_id: request.owner_id,
              business_unit_type: request.business_unit_type,
              business_unit_id: request.business_unit_id,
            },
          },
        };
        
      } else if (flow.id === 'pastos' || flow.id === 'gestor_pastos' || flow.name.toLowerCase().includes('pasto')) {
        // Payload para Gestor de Pastos
        payload = {
          form: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
            question: request.question,
          },
          streaming: false,
          overrideConfig: {
            sessionId: request.sessionId,
            vars: {
              user_id: request.owner_id,
              business_unit_type: "farm",
              business_unit_id: request.business_unit_id,
            },
          },
        };
        
      } else {
        // Default payload for other managers
        payload = {
          form: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
            question: request.question,
          },
          streaming: false,
          overrideConfig: {
            sessionId: request.sessionId,
            vars: {
              user_id: request.owner_id,
              business_unit_type: "farm",
              business_unit_id: request.business_unit_id,
            },
          },
        };
      }

      // Logs específicos para laboratório
      if (isLaboratoryManager) {

      }

      

      
      const messageResult = await this.sendMessageToBFF(flow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      // Log completo da resposta do Gestor Animal para depuração
      if (flow.id === 'animal') {
        try {
          
        } catch (err) {
          
        }
      }

      // Log complete response for health manager (sanidade) debugging
      if (flow.id === 'health') {
        // resposta de health disponível em result
      }

      // Detecção genérica: se algum gestor retornar manager_type com question, redirecionar via lista dinâmica
      try {
        let jsonText: string | null = typeof result?.text === 'string' ? result.text : null;
        if (jsonText) {
          const fenceIdx = jsonText.indexOf('```json');
          if (fenceIdx !== -1) {
            const endFenceIdx = jsonText.indexOf('```', fenceIdx + 6);
            if (endFenceIdx !== -1) {
              jsonText = jsonText.substring(fenceIdx + 7, endFenceIdx).trim();
            }
          }

          const trimmed = jsonText.trim();
          if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            const unwrapped = trimmed.slice(1, -1).trim();
            if (unwrapped.startsWith('{') && unwrapped.endsWith('}')) {
              jsonText = unwrapped;
            }
          }

          try {
            const parsed = JSON.parse(jsonText);
            const redirectData = parsed?.data ?? parsed;
            const managerTypeRaw = redirectData?.manager_type;
            const question = redirectData?.question;
            if (managerTypeRaw && question) {
              const flows = await this.getAvailableFlows(request.owner_id);
              const normalizedType = this.normalizeStr(managerTypeRaw);
              const normalizedId = this.normalizeStr(this.getManagerIdFromType(managerTypeRaw));

              const targetFlow = flows.find((f) => {
                const idNorm = this.normalizeStr(f.id);
                const nameNorm = this.normalizeStr(f.name);
                if (idNorm === normalizedId || idNorm === normalizedType) return true;
                if (nameNorm === normalizedType) return true;
                // Heurísticas por palavras-chave
                if (normalizedType.includes('perfil animal') || normalizedType.includes('animal')) {
                  return idNorm === 'animal' || nameNorm.includes('animal') || nameNorm.includes('perfil');
                }
                if (normalizedType.includes('nutricao') || normalizedType.includes('nutrição')) {
                  return idNorm === 'nutrition' || idNorm === 'nutricao' || nameNorm.includes('nutri');
                }
                if (normalizedType.includes('saude') || normalizedType.includes('sanidade')) {
                  return idNorm === 'health' || nameNorm.includes('saude') || nameNorm.includes('saúde') || nameNorm.includes('sanidade');
                }
                if (normalizedType.includes('ordenha')) {
                  return idNorm === 'dairy' || idNorm === 'ordenha' || nameNorm.includes('ordenha');
                }
                if (normalizedType.includes('reproducao') || normalizedType.includes('reprodução')) {
                  return idNorm === 'reproducao' || nameNorm.includes('reprod');
                }
                if (normalizedType.includes('master')) {
                  return idNorm === 'master' || nameNorm.includes('master');
                }
                return false;
              });

              if (targetFlow && targetFlow.id !== flow.id) {
                // Monta FlowiseRequest para reencaminhamento usando roteamento adequado
                const redirectReq: FlowiseRequest = {
                  question,
                  sessionId: request.sessionId,
                  organizationId: request.business_unit_id,
                  selectedFlowId: targetFlow.id,
                  context: {
                    owner_id: request.owner_id,
                    business_unit_type: request.business_unit_type ?? 'farm',
                    farmId: request.business_unit_id,
                    // Campos opcionais relevantes que podem ser necessários
                    muzzle_biometrics_base64: (request as any).muzzle_biometrics_base64,
                    cadastro_inserir_biometria: (request as any).cadastro_inserir_biometria,
                    cattle_tag: (request as any).cattle_tag,
                    insertExam: (request as any).insertExam,
                    examType: (request as any).examType,
                    cattleTag: (request as any).cattleTag,
                    collectionDate: (request as any).collectionDate,
                    examDate: (request as any).examDate,
                    attachments: (request as any).attachments,
                    examData: (request as any).examData,
                  },
                };
                return await this.processRedirectedManager(targetFlow, redirectReq);
              }
            }
          } catch (e) {}
        }
      } catch (_e) {}



      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: flow.id, name: flow.name },
        },
      };
    } catch (error) {
      console.error(`Error processing ${flow.id} manager:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Process a message through Flowise AI
   */
  async processMessage(request: FlowiseRequest): Promise<ProcessMessageResult> {
    try {
      // Garantir que gestores dinâmicos estejam carregados antes de selecionar o fluxo
      const userIdForManagers = request.context?.owner_id || request.context?.profileId || request.organizationId;
      if (!this.dynamicManagersCache || this.dynamicManagersCache.length === 0) {
        await this.getAvailableFlows(userIdForManagers);
      }

      // Get flow from dynamic cache or selected flow ID
      let flow: FlowConfig | undefined;
      if (request.selectedFlowId && this.dynamicManagersCache) {
        flow = this.dynamicManagersCache.find(f => f.id === request.selectedFlowId);
      }
      
      if (!flow && request.selectedFlowId) {
        flow = {
          id: request.selectedFlowId,
          name: request.selectedFlowId,
          endpoint: "",
          apiKey: "",
          organizationTypes: [],
        } as FlowConfig;
      }
      
      if (!flow) {
        return {
          success: false,
          error: "Selected flow not available - please refresh and try again"
        };
      }

      // Check if it's the permissions flow and use the specific function
      if (flow.id === "permissions") {
        // Create permissions request with required fields
        const permissionsRequest: PermissionsFlowiseRequest = {
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "permissions_manager",
          question: request.question,
          sessionId: request.sessionId,
        };

        return await this.processPermissionsMessage(permissionsRequest);
      }

      // Check if it's the health flow and use the specific payload
      if (flow.id === "health") {
        return await this.processSpecificManager(flow, {
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "health",
          question: request.question,
          sessionId: request.sessionId,
        });
      }

      // Check if it's the animal flow and use the specific payload
      if (
        flow.id === "animal" ||
        flow.id === "gestor_animal" ||
        flow.name.toLowerCase().includes("animal")
      ) {
        return await this.processSpecificManager(flow, {
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "animal",
          question: request.question,
          sessionId: request.sessionId,
          // Optional fields mapped from context if available
          muzzle_biometrics_base64: request.context?.muzzle_biometrics_base64,
          cadastro_inserir_biometria: request.context?.cadastro_inserir_biometria,
          cattle_tag: request.context?.cattle_tag,
        });
      }

      // Check if it's the laboratory flow and use the specific payload
      // Debug logs removidos
      const isLaboratoryManager =
        this.normalizeStr(flow.id).includes('laborator') ||
        this.normalizeStr(flow.name).includes('laborator');

      if (isLaboratoryManager) {
        return await this.processSpecificManager(flow, {
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "laboratory",
          question: request.question,
          sessionId: request.sessionId,
          insertExam: request.context?.insertExam,
          examType: request.context?.examType,
          cattleTag: request.context?.cattleTag,
          collectionDate: request.context?.collectionDate,
          examDate: request.context?.examDate,
          attachments: request.context?.attachments,
          examData: request.context?.examData,
        });
      }
      // Check if it's the ordenha flow and use the specific payload
      if (flow.id === "ordenha" || flow.id === "dairy" || flow.name.toLowerCase().includes('ordenha')) {
        return await this.processOrdernhaMessage({
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "ordenha",
          question: request.question,
          sessionId: request.sessionId,
        });
      }

      // Check if it's the nutricao flow and use the specific payload
      if (flow.id === "nutricao" || flow.id === "nutrition" || flow.name.toLowerCase().includes('nutricao') || flow.name.toLowerCase().includes('nutrição')) {
        return await this.processNutricaoMessage({
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "nutricao",
          question: request.question,
          sessionId: request.sessionId,
        });
      }

      // Check if it's the reproducao flow and use the specific payload
      if (flow.id === "gestor_reproducao" || flow.id === "reproducao" || flow.name.toLowerCase().includes('reprodução') || flow.name.toLowerCase().includes('reproducao')) {
        return await this.processReproducaoMessage({
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "gestor_reproducao",
          question: request.question,
          sessionId: request.sessionId,
        });
      }

      // Check if it's the batch flow and use the specific payload
      if (flow.id === "gestor_lotes" || flow.id === "gestor_lote" || flow.id === "batch" || flow.name.toLowerCase().includes('lote') || flow.id.toLowerCase().includes('lote')) {
        return await this.processBatchMessage({
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "batch",
          question: request.question,
          sessionId: request.sessionId,
        }, flow);
      }

      // Check if it's the pastos flow and use the specific payload
      if (flow.id === "pastos" || flow.id === "gestor_pastos" || flow.name.toLowerCase().includes('pasto')) {
        return await this.processPastosMessage({
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "gestor_pastos",
          question: request.question,
          sessionId: request.sessionId,
        }, flow);
      }

      // Check if it's the gestor_master flow and use the specific payload
      if (flow.id === "gestor_master" || flow.id === "master") {
        const masterResult = await this.processSpecificManager(flow, {
          owner_id:
            request.context?.owner_id ||
            request.context?.profileId ||
            "default_profile",
          business_unit_type: request.context?.business_unit_type || "farm",
          business_unit_id:
            request.organizationId || request.context?.farmId || "default_farm",
          manager_type: "gestor_master",
          question: request.question,
          sessionId: request.sessionId,
        });

        // Tenta redirecionamento automático a partir da resposta do master
        const text = masterResult.response?.text;

        if (text && typeof text === 'string') {
          let jsonText = text;
          if (jsonText.includes("```json")) {
            const jsonMatch = jsonText.match(/```json\s*([\s\S]*?)\s*```/);
            if (jsonMatch) {
              jsonText = jsonMatch[1].trim();
            }
          }
          // Unwrap external quotes if present: '\n'{...}'\n' or "{...}"
          const trimmed = jsonText.trim();
          if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            const unwrapped = trimmed.slice(1, -1).trim();
            if (unwrapped.startsWith('{') && unwrapped.endsWith('}')) {
              jsonText = unwrapped;
            }
          }
          
          try {
            const data = JSON.parse(jsonText);
            const redirectData = data.data || data;

            if (redirectData.manager_type && redirectData.question) {
              const userIdForManagers = request.context?.owner_id || request.context?.profileId || request.organizationId;
              const availableFlows = await this.getAvailableFlows(userIdForManagers);

              const managerTypeRaw = redirectData.manager_type || '';
              const managerType = this.normalizeStr(managerTypeRaw);
              const normalizedId = this.getManagerIdFromType(managerTypeRaw);
              const inferredId = this.inferManagerIdFromQuestion(redirectData.question);
              
              let targetFlow = availableFlows.find(f => {
                const flowId = this.normalizeStr(f.id);
                const flowName = this.normalizeStr(f.name);
                const match = (
                  flowName === managerType ||
                  flowId === this.normalizeStr(normalizedId) ||
                  flowId === managerType ||
                  (managerType.includes('perfil animal') && (flowId === 'animal' || flowName.includes('animal'))) ||
                  (managerType.includes('nutricao') && (flowId === 'nutrition' || flowId === 'nutricao' || flowName.includes('nutri'))) ||
                  (managerType.includes('saude') && (flowId === 'health' || flowName.includes('saude'))) ||
                  (managerType.includes('ordenha') && (flowId === 'dairy' || flowId === 'ordenha' || flowName.includes('ordenha'))) ||
                  (managerType.includes('reproducao') && (flowId === 'reproducao' || flowName.includes('reprod'))) ||
                  (managerType.includes('master') && (flowId === 'master' || flowName.includes('master')))
                );
                return match;
              });

              if (inferredId === 'pastos') {
                const isBatch = targetFlow && (this.normalizeStr(targetFlow.id) === 'batch' || this.normalizeStr(targetFlow.name).includes('lote'));
                if (!targetFlow || isBatch) {
                  const inferredFlowCandidate = availableFlows.find(f => this.normalizeStr(f.id) === 'pastos' || this.normalizeStr(f.name) === 'pastos');
                  if (inferredFlowCandidate) {
                    targetFlow = inferredFlowCandidate;
                  }
                }
              }

              // Fallback: if master was indicated or no match found, infer from question
              let inferredFlow: FlowConfig | undefined = undefined;
              if (!targetFlow || normalizedId === 'master' || managerType.includes('master')) {
                if (inferredId) {
                  inferredFlow = availableFlows.find(f => this.normalizeStr(f.id) === inferredId || this.normalizeStr(f.name) === inferredId);
                }
              }

              if (targetFlow) {
                const redirectRequest: FlowiseRequest = {
                  ...request,
                  question: redirectData.question,
                  selectedFlowId: targetFlow.id
                };
                return await this.processRedirectedManager(targetFlow, redirectRequest);
              } else if (inferredFlow) {
                const redirectRequest: FlowiseRequest = {
                  ...request,
                  question: redirectData.question,
                  selectedFlowId: inferredFlow.id
                };
                return await this.processRedirectedManager(inferredFlow, redirectRequest);
              } else {
                // Se não encontrou, retorna o masterResult original
                return masterResult;
              }
            }
          } catch (err) {
            // Ignora erros de parse e retorna masterResult
            return masterResult;
          }
        }

        return masterResult;
      }

      // Check if it's the nutrition flow and log the response
      if (flow.id === "gestor_nutricao" || flow.id === "nutrition" || flow.name.toLowerCase().includes('nutrição') || flow.name.toLowerCase().includes('nutricao')) {
        // Debug log removido
      }

      // For other managers, use the standard payload format
      const payload = {
        form: {
          farm_id: request.organizationId || "farm_001",
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            farm_id: request.organizationId || "farm_001",
          },
        },
      };

      // Debug log removido

      
      const messageResult = await this.sendMessageToBFF(flow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      // Log detailed response for nutrition manager
      // Logs detalhados removidos

      // Mantém a mesma lógica de parse que você já tinha
      if (result.text) {
        let jsonText = result.text;
        
        if (typeof jsonText === "string" && jsonText.includes("```json")) {
          const jsonMatch = jsonText.match(/```json\s*([\s\S]*?)\s*```/);
          if (jsonMatch) {
            jsonText = jsonMatch[1].trim();
          }
        }

        try {
          const data = JSON.parse(jsonText);

          // 🔄 REDIRECIONAMENTO AUTOMÁTICO DE GESTOR
          // Verifica se a resposta contém manager_type e question para redirecionamento
          const redirectData = data.data || data;
          
          if ((data.data && data.data.manager_type && data.data.question) || 
              (redirectData.manager_type && redirectData.question)) {
            
            
            // Busca o gestor correto na lista de gestores disponíveis
            const userIdForManagers = request.context?.owner_id || request.context?.profileId || request.organizationId;
            const availableFlows = await this.getAvailableFlows(userIdForManagers);

            // Normaliza o manager_type e tenta casar por nome exato e ID mapeado
            const managerTypeRaw = redirectData.manager_type || '';
            const managerType = this.normalizeStr(managerTypeRaw);
            const normalizedId = this.getManagerIdFromType(managerTypeRaw);

            const targetFlow = availableFlows.find(flow => {
              const flowId = this.normalizeStr(flow.id);
              const flowName = this.normalizeStr(flow.name);

              // 1) Casamento por nome exato (dinâmico)
              if (flowName === managerType) return true;

              // 2) Casamento por ID normalizado do manager_type
              if (flowId === this.normalizeStr(normalizedId)) return true;

              // 3) Casamento por ID igual ao manager_type bruto (casos onde id já é igual)
              if (flowId === managerType) return true;

              // 4) Heurística por palavras-chave (fallback)
              if (managerType.includes('perfil_animal') || managerType.includes('animal')) {
                return flowId === 'animal' || flowName.includes('animal') || flowName.includes('perfil');
              }
              if (managerType.includes('nutricao') || managerType.includes('nutrição')) {
                return flowId === 'nutrition' || flowId === 'nutricao' || flowName.includes('nutri');
              }
              if (managerType.includes('saude') || managerType.includes('sanidade')) {
                return flowId === 'health' || flowId === 'saude' || flowName.includes('saúde') || flowName.includes('sanidade');
              }
              if (managerType.includes('ordenha')) {
                return flowId === 'dairy' || flowId === 'ordenha' || flowName.includes('ordenha');
              }
              if (managerType.includes('reproducao') || managerType.includes('reprodução')) {
                return flowId === 'reproducao' || flowName.includes('reprod');
              }
              if (managerType.includes('master')) {
                return flowId === 'master' || flowName.includes('master');
              }

              return false;
            });

            // Fallback: if master was indicated or no match found, infer from question
            let inferredFlow: FlowConfig | undefined = undefined;
            if (!targetFlow || normalizedId === 'master' || managerType.includes('master')) {
              const inferredId = this.inferManagerIdFromQuestion(redirectData.question);
              if (inferredId) {
                inferredFlow = availableFlows.find(f => this.normalizeStr(f.id) === inferredId || this.normalizeStr(f.name) === inferredId);
              }
            }

            if (targetFlow) {
              
              // Cria uma nova requisição para o gestor correto
              const redirectRequest: FlowiseRequest = {
                ...request,
                question: redirectData.question,
                selectedFlowId: targetFlow.id
              };

              
              
              // Chama o método específico do gestor com o payload correto
              return await this.processRedirectedManager(targetFlow, redirectRequest);
            } else if (inferredFlow) {
              const redirectRequest: FlowiseRequest = {
                ...request,
                question: redirectData.question,
                selectedFlowId: inferredFlow.id
              };
              return await this.processRedirectedManager(inferredFlow, redirectRequest);
            } else {
              console.warn('⚠️ Gestor não encontrado para:', redirectData.manager_type);
              
              // Se não encontrou o gestor, retorna a resposta original
              return {
                success: true,
                response: {
                  text: `Gestor "${redirectData.manager_type}" não encontrado. Gestores disponíveis: ${availableFlows.map(f => f.name).join(', ')}`,
                  sessionId: request.sessionId,
                  sourceDocuments: undefined,
                  agentReasoning: undefined,
                  rawResponse: data,
                },
              };
            }
          }

          // Se tem content_type, preserva a resposta JSON completa
          if (data.content_type) {
            return {
              success: true,
              response: {
                text: jsonText, // Preserva o JSON completo para processamento no MessageView
                sessionId: request.sessionId,
                sourceDocuments: undefined,
                agentReasoning: undefined,
                rawResponse: data, // Preserva os dados completos
              },
            };
          }

          // Para respostas sem content_type, usa a lógica antiga
          const agentResponse = data.data as AgentResponse;
          return {
            success: !isAgentResponseError(agentResponse),
            response: {
              text: extractChatText(agentResponse),
              sessionId: extractSessionId(agentResponse) || request.sessionId,
              sourceDocuments: undefined,
              agentReasoning: undefined,
              rawResponse: agentResponse,
            },
          };
        } catch (_parseError) {
          return {
            success: true,
            response: {
              text: result.text,
              sessionId: request.sessionId,
              sourceDocuments: undefined,
              agentReasoning: undefined,
            },
          };
        }
      } else {
        return {
          success: true,
          response: {
            text: result.text || result.message || "No response received",
            sessionId: result.sessionId || request.sessionId,
            sourceDocuments: result.sourceDocuments,
            agentReasoning: result.agentReasoning,
          },
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Process message specifically for MUU Permissões with custom payload
   */
  async processPermissionsMessage(
    request: PermissionsFlowiseRequest
  ): Promise<ProcessMessageResult> {
    try {
      // Get dynamic managers
      const flows = await this.getAvailableFlows(request.owner_id);
      
      // Find the manager based on manager_type
      let targetFlow = flows.find(flow => 
        flow.id === request.manager_type || 
        flow.name.toLowerCase().includes(request.manager_type.toLowerCase()) ||
        flow.id === this.getManagerIdFromType(request.manager_type)
      );

      if (!targetFlow) {
        const desired = this.normalizeStr(request.manager_type);
        if (desired.includes('laborator')) {
          targetFlow = flows.find(flow =>
            this.normalizeStr(flow.id).includes('laborator') ||
            this.normalizeStr(flow.name).includes('laborator')
          );
        }
      }

      if (!targetFlow) {
        console.error(`Manager not found in dynamic managers: ${request.manager_type}`);
        return {
          success: false,
          error: `Gestor ${request.manager_type} não encontrado nos gestores dinâmicos`
        };
      }

      // Use the specific manager processor
      return await this.processSpecificManager(targetFlow, request);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Process message specifically for Gestor de Sanidade with custom payload
   */
  async processHealthMessage(
    request: PermissionsFlowiseRequest
  ): Promise<ProcessMessageResult> {
    try {
      // Get the flow based on manager_type
      // No hardcoded flows available - API only
      console.error(`No manager flow available for type: ${request.manager_type} - API required`);
      return {
        success: false,
        error: `Manager flow not available for type: ${request.manager_type} - dynamic managers required`
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Process message specifically for Gestor de Ordenha with custom payload
   */
  async processOrdernhaMessage(
    request: PermissionsFlowiseRequest,
    flowOverride?: FlowConfig
  ): Promise<ProcessMessageResult> {
    try {
      // Get dynamic managers
      const flows = await this.getAvailableFlows(request.owner_id);
      
      // Find the ordenha manager (or use override)
      const ordenhaFlow = flowOverride || flows.find(flow => 
        flow.id === 'ordenha' || 
        flow.name.toLowerCase().includes('ordenha') ||
        flow.id === this.getManagerIdFromType('ordenha')
      );

      if (!ordenhaFlow) {
        console.error('Ordenha manager not found in dynamic managers');
        return {
          success: false,
          error: "Gestor de Ordenha não encontrado nos gestores dinâmicos"
        };
      }

      // Create the specific payload for ordenha manager
      const payload = {
        form: {
          user_id: request.owner_id,
          business_unit_type: "farm",
          business_unit_id: request.business_unit_id,
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
          },
        },
      };

      // Debug log removido

      
      const messageResult = await this.sendMessageToBFF(ordenhaFlow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      // Log complete response for ordenha manager debugging
      // Logs detalhados removidos

      // Tentativa de redirecionamento automático para gestor_master via lista dinâmica
      try {
        let jsonText: string | null = typeof result?.text === 'string' ? result.text : null;
        if (jsonText) {
          // Extrai bloco ```json ... ``` se existir
          const fenceIdx = jsonText.indexOf('```json');
          if (fenceIdx !== -1) {
            const endFenceIdx = jsonText.indexOf('```', fenceIdx + 6);
            if (endFenceIdx !== -1) {
              jsonText = jsonText.substring(fenceIdx + 7, endFenceIdx).trim();
            }
          }

          // Desembrulhar aspas externas, se o texto vier como string JSON dentro de aspas
          const trimmed = jsonText.trim();
          if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            const unwrapped = trimmed.slice(1, -1).trim();
            if (unwrapped.startsWith('{') && unwrapped.endsWith('}')) {
              jsonText = unwrapped;
            }
          }

          try {
            const parsed = JSON.parse(jsonText);
            const redirectData = parsed?.data ?? parsed;
            const managerTypeRaw = redirectData?.manager_type;
            const question = redirectData?.question;
            if (managerTypeRaw && question) {
              const normalizedType = this.normalizeStr(managerTypeRaw);
              // Se indicar master, procurar especificamente gestor_master na lista dinâmica
              if (normalizedType.includes('master')) {
                const flows = await this.getAvailableFlows(request.owner_id);
                const targetFlow = flows.find((f) => {
                  const idNorm = this.normalizeStr(f.id);
                  const nameNorm = this.normalizeStr(f.name);
                  // Preferir match exato do id/nome retornado antes de fallbacks
                  return (
                    idNorm === this.normalizeStr(managerTypeRaw) ||
                    nameNorm === normalizedType ||
                    idNorm === this.normalizeStr(this.getManagerIdFromType(managerTypeRaw)) ||
                    nameNorm.includes('master') ||
                    idNorm.includes('master')
                  );
                });

                if (targetFlow && targetFlow.id !== ordenhaFlow.id) {
                  const redirectReq: PermissionsFlowiseRequest = {
                    owner_id: request.owner_id,
                    business_unit_type: 'farm',
                    business_unit_id: request.business_unit_id,
                    manager_type: 'gestor_master',
                    question,
                    sessionId: request.sessionId,
                  };
                  return await this.processSpecificManager(targetFlow, redirectReq);
                }
              }
            }
          } catch (e) {
            // ignore parse errors
          }
        }
      } catch (_e) {
        // ignore unexpected errors na camada de redirecionamento
      }

      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: ordenhaFlow.id, name: ordenhaFlow.name },
        },
      };
    } catch (error) {
      console.error(`Error processing ordenha manager:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  async processNutricaoMessage(
    request: PermissionsFlowiseRequest,
    flowOverride?: FlowConfig
  ): Promise<ProcessMessageResult> {
    try {
      // Get dynamic managers
      const flows = await this.getAvailableFlows(request.owner_id);
      
      // Find the nutricao manager (or use override)
      const nutricaoFlow = flowOverride || flows.find(flow => 
        flow.id === 'nutricao' || 
        flow.name.toLowerCase().includes('nutricao') ||
        flow.name.toLowerCase().includes('nutrição') ||
        flow.id === this.getManagerIdFromType('nutricao')
      );

      if (!nutricaoFlow) {
        console.error('Nutricao manager not found in dynamic managers');
        return {
          success: false,
          error: "Gestor de Nutrição não encontrado nos gestores dinâmicos"
        };
      }

      // Create the specific payload for nutricao manager (same as ordenha)
      const payload = {
        form: {
          user_id: request.owner_id,
          business_unit_type: "farm",
          business_unit_id: request.business_unit_id,
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
          },
        },
      };

      // Debug log removido

      
      const messageResult = await this.sendMessageToBFF(nutricaoFlow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      // Log complete response for nutricao manager debugging
      // Logs detalhados removidos

      // Tentativa de redirecionamento automático para gestor_master via lista dinâmica
      try {
        let jsonText: string | null = typeof result?.text === 'string' ? result.text : null;
        if (jsonText) {
          const fenceIdx = jsonText.indexOf('```json');
          if (fenceIdx !== -1) {
            const endFenceIdx = jsonText.indexOf('```', fenceIdx + 6);
            if (endFenceIdx !== -1) {
              jsonText = jsonText.substring(fenceIdx + 7, endFenceIdx).trim();
            }
          }

          const trimmed = jsonText.trim();
          if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            const unwrapped = trimmed.slice(1, -1).trim();
            if (unwrapped.startsWith('{') && unwrapped.endsWith('}')) {
              jsonText = unwrapped;
            }
          }

          try {
            const parsed = JSON.parse(jsonText);
            const redirectData = parsed?.data ?? parsed;
            const managerTypeRaw = redirectData?.manager_type;
            const question = redirectData?.question;
            if (managerTypeRaw && question) {
              const normalizedType = this.normalizeStr(managerTypeRaw);
              if (normalizedType.includes('master')) {
                const flows = await this.getAvailableFlows(request.owner_id);
                const targetFlow = flows.find((f) => {
                  const idNorm = this.normalizeStr(f.id);
                  const nameNorm = this.normalizeStr(f.name);
                  return (
                    idNorm === this.normalizeStr(managerTypeRaw) ||
                    nameNorm === normalizedType ||
                    idNorm === this.normalizeStr(this.getManagerIdFromType(managerTypeRaw)) ||
                    nameNorm.includes('master') ||
                    idNorm.includes('master')
                  );
                });

                if (targetFlow && targetFlow.id !== nutricaoFlow.id) {
                  const redirectReq: PermissionsFlowiseRequest = {
                    owner_id: request.owner_id,
                    business_unit_type: 'farm',
                    business_unit_id: request.business_unit_id,
                    manager_type: 'gestor_master',
                    question,
                    sessionId: request.sessionId,
                  };
                  return await this.processSpecificManager(targetFlow, redirectReq);
                }
              }
            }
          } catch (e) {}
        }
      } catch (_e) {}

      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: nutricaoFlow.id, name: nutricaoFlow.name },
        },
      };
    } catch (error) {
      console.error(`Error processing nutricao manager:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  async processReproducaoMessage(
    request: PermissionsFlowiseRequest,
    flowOverride?: FlowConfig
  ): Promise<ProcessMessageResult> {
    try {
      // Get dynamic managers
      const flows = await this.getAvailableFlows(request.owner_id);
      
      // Find the reproducao manager (or use override)
      const reproducaoFlow = flowOverride || flows.find(flow => 
        flow.id === 'gestor_reproducao' || 
        flow.name.toLowerCase().includes('reprodução') ||
        flow.name.toLowerCase().includes('reproducao') ||
        flow.id === this.getManagerIdFromType('gestor_reproducao')
      );

      if (!reproducaoFlow) {
        console.error('Reprodução manager not found in dynamic managers');
        return {
          success: false,
          error: "Gestor de Reprodução não encontrado nos gestores dinâmicos"
        };
      }

      // Create the specific payload for reproducao manager
      const payload = {
        form: {
          user_id: request.owner_id,
          business_unit_type: "farm",
          business_unit_id: request.business_unit_id,
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
          },
        },
      };

      // Debug log removido

      
      const messageResult = await this.sendMessageToBFF(reproducaoFlow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      // Log complete response for reproducao manager debugging
      
      
      // Logs detalhados removidos

      // Tentativa de redirecionamento automático para gestor_master via lista dinâmica
      try {
        let jsonText: string | null = typeof result?.text === 'string' ? result.text : null;
        if (jsonText) {
          const fenceIdx = jsonText.indexOf('```json');
          if (fenceIdx !== -1) {
            const endFenceIdx = jsonText.indexOf('```', fenceIdx + 6);
            if (endFenceIdx !== -1) {
              jsonText = jsonText.substring(fenceIdx + 7, endFenceIdx).trim();
            }
          }

          const trimmed = jsonText.trim();
          if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
            const unwrapped = trimmed.slice(1, -1).trim();
            if (unwrapped.startsWith('{') && unwrapped.endsWith('}')) {
              jsonText = unwrapped;
            }
          }

          try {
            const parsed = JSON.parse(jsonText);
            const redirectData = parsed?.data ?? parsed;
            const managerTypeRaw = redirectData?.manager_type;
            const question = redirectData?.question;
            if (managerTypeRaw && question) {
              const normalizedType = this.normalizeStr(managerTypeRaw);
              if (normalizedType.includes('master')) {
                const flows = await this.getAvailableFlows(request.owner_id);
                const targetFlow = flows.find((f) => {
                  const idNorm = this.normalizeStr(f.id);
                  const nameNorm = this.normalizeStr(f.name);
                  return (
                    idNorm === this.normalizeStr(managerTypeRaw) ||
                    nameNorm === normalizedType ||
                    idNorm === this.normalizeStr(this.getManagerIdFromType(managerTypeRaw)) ||
                    nameNorm.includes('master') ||
                    idNorm.includes('master')
                  );
                });

                if (targetFlow && targetFlow.id !== reproducaoFlow.id) {
                  const redirectReq: PermissionsFlowiseRequest = {
                    owner_id: request.owner_id,
                    business_unit_type: 'farm',
                    business_unit_id: request.business_unit_id,
                    manager_type: 'gestor_master',
                    question,
                    sessionId: request.sessionId,
                  };
                  return await this.processSpecificManager(targetFlow, redirectReq);
                }
              }
            }
          } catch (e) {}
        }
      } catch (_e) {}

      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: reproducaoFlow.id, name: reproducaoFlow.name },
        },
      };
    } catch (error) {
      console.error(`Error processing reproducao manager:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Select appropriate flow based on organization type
   */
  private selectFlow(organizationType?: string): FlowConfig | null {
    // No hardcoded flows - return null
    return null;
  }

  /**
   * Get available flows - uses dynamic managers if available, otherwise emergency fallback
   */
  async getAvailableFlows(userId?: string): Promise<FlowConfig[]> {
    const now = Date.now();

    // If we already have cached managers, return them when userId is missing
    if (!userId) {
      if (this.dynamicManagersCache && this.dynamicManagersCache.length > 0) {
        return this.dynamicManagersCache; // allow redirection using cached flows
      }
      console.warn('No userId provided and no cache available - returning empty array');
      return [];
    }

    // Check cache validity when userId is provided
    if (
      this.dynamicManagersCache &&
      this.dynamicManagersCache.length > 0 &&
      (now - this.cacheTimestamp) < this.CACHE_DURATION
    ) {
      return this.dynamicManagersCache;
    }

    // Fetch fresh dynamic managers with userId
    const dynamicManagers = await this.fetchDynamicManagers(userId);
    if (dynamicManagers.length > 0) {
      return dynamicManagers;
    }

    // Fallback to whatever cache we have, even if expired
    return this.dynamicManagersCache || [];
  }

  /**
   * Get flow by ID
   */
  getFlowById(flowId: string): FlowConfig | undefined {
    // No hardcoded flows available - check dynamic cache only
    if (this.dynamicManagersCache) {
      return this.dynamicManagersCache.find(f => f.id === flowId);
    }
    return undefined;
  }

  /**
   * Processa redirecionamento automático para o gestor específico com payload correto
   */
  private async processRedirectedManager(
    targetFlow: FlowConfig, 
    request: FlowiseRequest
  ): Promise<ProcessMessageResult> {
    

    // Cria o payload base para todos os gestores
    const basePayload = {
      owner_id: request.context?.owner_id || request.context?.profileId || "default_profile",
      business_unit_type: request.context?.business_unit_type || "farm",
      business_unit_id: request.organizationId || request.context?.farmId || "default_farm",
      question: request.question,
      sessionId: request.sessionId,
    };

    // Roteamento específico para cada gestor com seu payload correto
    switch (targetFlow.id) {
      case "permissions":
        return await this.processPermissionsMessage({
          ...basePayload,
          manager_type: "permissions_manager",
        });

      case "health":
        return await this.processSpecificManager(targetFlow, {
          ...basePayload,
          manager_type: "health",
        });

      case "animal":
        return await this.processSpecificManager(targetFlow, {
          ...basePayload,
          manager_type: "animal",
          // Campos específicos do animal
          muzzle_biometrics_base64: request.context?.muzzle_biometrics_base64,
          cadastro_inserir_biometria: request.context?.cadastro_inserir_biometria,
          cattle_tag: request.context?.cattle_tag,
        });

      case "laboratory":
        return await this.processSpecificManager(targetFlow, {
          ...basePayload,
          manager_type: "laboratory",
          // Campos específicos do laboratório
          insertExam: request.context?.insertExam,
          examType: request.context?.examType,
          cattleTag: request.context?.cattleTag,
          collectionDate: request.context?.collectionDate,
          examDate: request.context?.examDate,
          attachments: request.context?.attachments,
          examData: request.context?.examData,
        });

      case "ordenha":
      case "dairy":
        return await this.processOrdernhaMessage({
          ...basePayload,
          manager_type: "ordenha",
        }, targetFlow);

      case "nutricao":
      case "nutrition":
        return await this.processNutricaoMessage({
          ...basePayload,
          manager_type: "nutricao",
        }, targetFlow);

      case "gestor_reproducao":
      case "reproducao":
        return await this.processReproducaoMessage({
          ...basePayload,
          manager_type: "gestor_reproducao",
        }, targetFlow);

      case "gestor_lotes":
      case "gestor_lote":
      case "batch":
        return await this.processBatchMessage({
          ...basePayload,
          manager_type: "batch",
        }, targetFlow);

      case "pastos":
      case "gestor_pastos":
        return await this.processPastosMessage({
          ...basePayload,
          manager_type: "gestor_pastos",
        }, targetFlow);

      case "gestor_master":
      case "master":
        return await this.processSpecificManager(targetFlow, {
          ...basePayload,
          manager_type: "gestor_master",
        });

      default:
        // Para gestores não mapeados, usa processSpecificManager genérico
        
        return await this.processSpecificManager(targetFlow, {
          ...basePayload,
          manager_type: targetFlow.id,
        });
    }
  }

  async processBatchMessage(
    request: PermissionsFlowiseRequest,
    flowOverride?: FlowConfig
  ): Promise<ProcessMessageResult> {
    try {
      const flow = flowOverride || this.selectFlow();
      if (!flow) {
        return {
          success: false,
          error: "No flow configuration available for batch manager",
        };
      }

      // Payload específico para Gestor de Lotes conforme solicitado
      const payload = {
        form: {
          user_id: request.owner_id,
          business_unit_type: "farm",
          business_unit_id: request.business_unit_id,
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
          },
        },
      };

      

      
      const messageResult = await this.sendMessageToBFF(flow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      

      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: flow.id, name: flow.name },
        },
      };
    } catch (error) {
      console.error("Error processing batch message:", error);
      const msg = error instanceof Error ? error.message.toLowerCase() : "";
      const inferred = this.inferManagerIdFromQuestion(request.question);
      if (inferred === 'pastos' || msg.includes('status: 504') || msg.includes('timeout')) {
        try {
          const flows = await this.getAvailableFlows(request.owner_id);
          const pastosFlow = flows.find(f => this.normalizeStr(f.id) === 'pastos' || this.normalizeStr(f.name) === 'pastos');
          if (pastosFlow) {
            return await this.processPastosMessage({
              owner_id: request.owner_id,
              business_unit_type: 'farm',
              business_unit_id: request.business_unit_id,
              manager_type: 'gestor_pastos',
              question: request.question,
              sessionId: request.sessionId,
            }, pastosFlow);
          }
        } catch (_fallbackErr) {
          // ignore and return original error
        }
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  async processPastosMessage(
    request: PermissionsFlowiseRequest,
    flowOverride?: FlowConfig
  ): Promise<ProcessMessageResult> {
    try {
      const flow = flowOverride || this.selectFlow();
      if (!flow) {
        return {
          success: false,
          error: "No flow configuration available for pastos manager",
        };
      }

      // Payload específico para Gestor de Pastos conforme solicitado
      const payload = {
        form: {
          user_id: request.owner_id,
          business_unit_type: "farm",
          business_unit_id: request.business_unit_id,
          question: request.question,
        },
        streaming: false,
        overrideConfig: {
          sessionId: request.sessionId,
          vars: {
            user_id: request.owner_id,
            business_unit_type: "farm",
            business_unit_id: request.business_unit_id,
          },
        },
      };

      

      const messageResult = await this.sendMessageToBFF(flow.id, payload);
      if (!messageResult.success || !messageResult.response) {
        throw new Error(messageResult.error || "Unknown error");
      }
      const result = (messageResult.response.rawResponse || {}) as any;

      return {
        success: true,
        response: {
          text: result.text || result.message || "No response received",
          sessionId: result.sessionId || request.sessionId,
          sourceDocuments: result.sourceDocuments,
          agentReasoning: result.agentReasoning,
          rawResponse: result,
          flowUsed: { id: flow.id, name: flow.name },
        },
      };
    } catch (error) {
      console.error("Error processing pastos message:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}

// Export singleton instance
export const flowiseService = new FlowiseService();
